import { CartLinesUpdateEvent } from '@shopify/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

const TIMER_MINUTES = 15;
const TIMER_KEY = 'mmCartTimerEnd';

const DRAWER_SECTION = 'cart-drawer-section';

// Keep an untouched fetch for our own requests (window.fetch is wrapped further down).
const nativeFetch = window.fetch.bind(window);

const routes = () => window.Theme?.routes || {};
const root = () => document.querySelector('[data-mm-cart]');

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids).join(',');
}

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'GBP';
  const locale = document.documentElement.lang || 'en';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return (cents / 100).toFixed(2);
  }
}

function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

async function fetchCart() {
  const res = await nativeFetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  return res.json();
}

/**
 * Sends a cart mutation and announces it with the theme's CartLinesUpdateEvent, so the
 * drawer section re-renders and the header cart count updates exactly like a native change.
 */
async function mutateCart(source, { url, body, action, lines }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));

  const cartEl = root();
  cartEl?.classList.add('is-loading');

  try {
    const res = await nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionIds(), sections_url: window.location.pathname + window.location.search }),
    });
    const data = await res.json();
    if (!res.ok || (data.status && data.message)) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }

    const cart = data.token && Array.isArray(data.items) ? data : await fetchCart();
    let cartForEvent = null;
    try {
      cartForEvent = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
    } catch (error) {
      console.warn('[mm-cart-drawer] could not build cart event payload', error);
    }
    deferred.resolve({
      cart: cartForEvent,
      detail: {
        sections: data.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'mm-cart-drawer',
        didError: false,
      },
    });
    await refreshDrawer(data.sections?.[DRAWER_SECTION]);
    updateCartBubbles(cart.item_count);
    return { ok: true };
  } catch (error) {
    console.error('[mm-cart-drawer]', error);
    deferred.reject(error);
    return { ok: false, message: error.message };
  } finally {
    root()?.classList.remove('is-loading');
  }
}

/** Snapshot of what the drawer shows, used to tell whether it has already been redrawn. */
function drawerState(scope) {
  const count = scope.querySelector('[ref="cartItemCount"]')?.textContent?.trim() ?? '';
  const total = scope.querySelector('.mm-cart__checkout-btn')?.textContent?.trim() ?? '';
  const qtys = Array.from(scope.querySelectorAll('[data-mm-qty-input]'))
    .map((input) => `${input.getAttribute('data-key')}:${input.getAttribute('value')}`)
    .join('|');
  return `${count}#${total}#${qtys}`;
}

/**
 * Redraws the drawer from the section HTML returned by the cart request, unless the theme's
 * own listener already did. Uses the theme's morph so the timer and upsell keep their state.
 */
async function refreshDrawer(sectionHtml) {
  if (!sectionHtml) return;

  // Give the theme's listener a chance to morph first.
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  const fresh = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.cart-drawer__inner');
  const current = document.querySelector('#cart-drawer .cart-drawer__inner');
  if (!fresh || !current) return;
  if (drawerState(current) === drawerState(fresh) && current.className === fresh.className) return;

  try {
    morph(current, fresh, { ...MORPH_OPTIONS, childrenOnly: false });
  } catch (error) {
    console.warn('[mm-cart-drawer] morph failed, replacing drawer content', error);
    current.replaceWith(document.importNode(fresh, true));
  }
}

/** Updates every header cart-count bubble to the given item count. */
function updateCartBubbles(count) {
  document.querySelectorAll('.cart-bubble').forEach((bubble) => {
    bubble.classList.toggle('visually-hidden', count === 0);
    if (count <= 99) bubble.setAttribute('data-maintain-ratio', '');
    else bubble.removeAttribute('data-maintain-ratio');
  });
  document.querySelectorAll('.cart-bubble__text-count').forEach((el) => {
    el.textContent = count > 0 ? String(count) : '';
    el.classList.toggle('hidden', count === 0);
  });
}

/* ---------- Catch add-to-cart from ANY button or app ---------- */

const CART_MUTATION_PATH = /\/cart\/(add|change|update|clear)(\.js|\.json)?\/?$/;
let syncTimer = 0;
let syncShouldOpen = false;

function cartMutationKind(url, method) {
  if (String(method || 'GET').toUpperCase() !== 'POST') return null;
  let path = '';
  try {
    path = new URL(String(url), window.location.href).pathname;
  } catch {
    return null;
  }
  const match = path.match(CART_MUTATION_PATH);
  return match ? match[1] : null;
}

function scheduleCartSync(kind) {
  if (kind === 'add') syncShouldOpen = true;
  clearTimeout(syncTimer);
  syncTimer = setTimeout(runCartSync, 150);
}

function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || drawer.hasAttribute('open') || typeof drawer.open !== 'function') return;

  // If a quick-add (or other) pop-up is open, close it and then open the drawer right away.
  const blocking = Array.from(document.querySelectorAll('dialog[open]')).find(
    (dialog) => !drawer.contains(dialog) && dialog.matches(':modal')
  );
  if (!blocking) {
    drawer.open();
    return;
  }

  let opened = false;
  const openOnce = () => {
    if (opened) return;
    opened = true;
    if (!drawer.hasAttribute('open')) drawer.open();
  };
  blocking.addEventListener('close', () => setTimeout(openOnce, 30), { once: true });
  setTimeout(openOnce, 700); // fallback if the pop-up never fires "close"

  // Prefer the theme component's own close method so scroll lock and focus are restored.
  const host = blocking.closest('quick-add-dialog, dialog-component') || blocking.parentElement;
  if (host && typeof host.closeDialog === 'function') host.closeDialog();
  else if (host && typeof host.close === 'function' && host !== blocking) host.close();
  else blocking.close();
}

async function runCartSync() {
  const shouldOpen = syncShouldOpen;
  syncShouldOpen = false;
  try {
    const params = new URLSearchParams(window.location.search);
    params.set('sections', DRAWER_SECTION);
    const res = await nativeFetch(`${window.location.pathname}?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    const sections = res.ok ? await res.json() : {};
    await refreshDrawer(sections?.[DRAWER_SECTION]);

    const cart = await fetchCart();
    updateCartBubbles(cart.item_count);
    if (shouldOpen && cart.item_count > 0) openCartDrawer();
  } catch (error) {
    console.warn('[mm-cart-drawer] cart sync failed', error);
  }
}

// Watch fetch() cart requests made by the theme or any app.
window.fetch = async function (input, init) {
  const response = await nativeFetch(input, init);
  try {
    const url = typeof input === 'string' || input instanceof URL ? input : input?.url;
    const method = init?.method || (input instanceof Request ? input.method : 'GET');
    const kind = cartMutationKind(url, method);
    if (kind && response.ok) scheduleCartSync(kind);
  } catch {}
  return response;
};

// Watch XMLHttpRequest cart requests (jQuery-based apps).
const nativeXhrOpen = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function (method, url, ...rest) {
  const kind = cartMutationKind(url, method);
  if (kind) {
    this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) scheduleCartSync(kind);
    });
  }
  return nativeXhrOpen.call(this, method, url, ...rest);
};

// Plain (non-AJAX) add-to-cart forms would reload to /cart; add them in place instead.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
  if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
  if (form.closest('product-form-component')) return; // the theme's own form handles itself
  if (!document.getElementById('cart-drawer')) return;

  event.preventDefault();
  const root = window.Shopify?.routes?.root || '/';
  nativeFetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: new FormData(form),
  })
    .then((res) => {
      if (res.ok) scheduleCartSync('add');
      else form.submit();
    })
    .catch(() => form.submit());
});

function changeLine(source, key, quantity) {
  return mutateCart(source, {
    url: routes().cart_change_url || '/cart/change.js',
    body: { id: key, quantity },
    action: quantity > 0 ? 'update' : 'remove',
    lines: [{ id: key, quantity }],
  });
}

/* ---------- Delegated events (survive section re-renders) ---------- */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || !target.closest('[data-mm-cart]')) return;

  const close = target.closest('[data-mm-close]');
  if (close) {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
    else close.closest('dialog')?.close();
    return;
  }

  const qtyBtn = target.closest('[data-mm-qty]');
  if (qtyBtn instanceof HTMLElement) {
    event.preventDefault();
    const quantity = Math.max(0, parseInt(qtyBtn.dataset.mmQty, 10) || 0);
    changeLine(qtyBtn, qtyBtn.dataset.key, quantity);
  }
});

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-mm-qty-input]')) return;
  const quantity = Math.max(0, parseInt(input.value, 10) || 0);
  changeLine(input, input.dataset.key, quantity);
});

/* ---------- Reservation timer ---------- */

class MMCartTimer extends HTMLElement {
  connectedCallback() {
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  tick() {
    const now = Date.now();
    let end = 0;
    try {
      end = Number(sessionStorage.getItem(TIMER_KEY)) || 0;
    } catch {}
    if (!end || end <= now) {
      // Start (or restart after reaching 00:00) a fresh countdown.
      end = now + TIMER_MINUTES * 60 * 1000;
      try {
        sessionStorage.setItem(TIMER_KEY, String(end));
      } catch {}
    }
    const secs = Math.max(0, Math.ceil((end - now) / 1000));
    const text = `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`;
    const el = this.querySelector('[data-mm-time]');
    if (el && el.textContent !== text) el.textContent = text;
  }
}

if (!customElements.get('mm-cart-timer')) customElements.define('mm-cart-timer', MMCartTimer);

/* ---------- "Complete Your Order" upsell ---------- */

const SPINNER_CSS = `
  .mm-upsell__add { display: inline-flex; align-items: center; justify-content: center; }
  .mm-upsell__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: mm-upsell-spin 0.7s linear infinite;
  }
  @keyframes mm-upsell-spin { to { transform: rotate(360deg); } }
`;
if (!document.getElementById('mm-upsell-spinner-css')) {
  const spinnerStyle = document.createElement('style');
  spinnerStyle.id = 'mm-upsell-spinner-css';
  spinnerStyle.textContent = SPINNER_CSS;
  document.head.append(spinnerStyle);
}

const ICON_PREV =
  '<svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true"><path d="M8 2L2 8l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ICON_NEXT =
  '<svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true"><path d="M2 2l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class MMCartUpsell extends HTMLElement {
  static observedAttributes = ['data-product-ids'];

  products = [];
  index = 0;
  loadId = 0;

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== null && oldValue !== newValue && this.isConnected) this.load();
  }

  async fetchRecommendations(productId, intent) {
    const base = routes().product_recommendations_url || `${window.Shopify?.routes?.root || '/'}recommendations/products`;
    try {
      const res = await fetch(`${base}.json?product_id=${productId}&limit=10&intent=${intent}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.products || [];
    } catch {
      return [];
    }
  }

  async load() {
    const loadId = ++this.loadId;
    const inCart = (this.dataset.productIds || '').split(',').filter(Boolean);
    if (!inCart.length) {
      this.innerHTML = '';
      return;
    }

    let products = await this.fetchRecommendations(inCart[0], 'complementary');
    if (!products.length) products = await this.fetchRecommendations(inCart[0], 'related');
    if (loadId !== this.loadId) return;

    this.products = products.filter((p) => p.available && !inCart.includes(String(p.id)));
    if (!this.products.length) {
      this.innerHTML = '';
      return;
    }

    this.index = Math.min(this.index, this.products.length - 1);
    this.render();
  }

  get product() {
    return this.products[this.index];
  }

  selectedVariant() {
    const select = this.querySelector('[data-mm-upsell-variant]');
    const id = select instanceof HTMLSelectElement ? Number(select.value) : null;
    const variants = this.product.variants.filter((v) => v.available);
    return variants.find((v) => v.id === id) || variants[0];
  }

  imageFor(product, variant) {
    let src = variant?.featured_image?.src || product.featured_image || '';
    if (!src) return '';
    if (src.startsWith('//')) src = `https:${src}`;
    return `${src}${src.includes('?') ? '&' : '?'}width=180`;
  }

  priceHtml(variant) {
    const compare = variant.compare_at_price && variant.compare_at_price > variant.price
      ? `<s>${formatMoney(variant.compare_at_price)}</s>` : '';
    return `${compare}<span>${formatMoney(variant.price)}</span>`;
  }

  render() {
    const product = this.product;
    const variants = product.variants.filter((v) => v.available);
    const variant = variants[0];
    const multiple = this.products.length > 1;
    const showSelect = !(variants.length === 1 && variant.title === 'Default Title');

    this.innerHTML = `
      <h3 class="mm-upsell__title">Complete Your Order</h3>
      <div class="mm-upsell__row">
        ${multiple ? `<button type="button" class="mm-upsell__arrow mm-upsell__arrow--prev" data-mm-upsell-nav="-1" aria-label="Previous product">${ICON_PREV}</button>` : ''}
        <div class="mm-upsell__slide">
          <a class="mm-upsell__img" href="${escapeHtml(product.url)}">
            <img src="${escapeHtml(this.imageFor(product, variant))}" alt="${escapeHtml(product.title)}" loading="lazy" width="60" height="90">
          </a>
          <div class="mm-upsell__body">
            <a class="mm-upsell__name" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
            <div class="mm-upsell__price" data-mm-upsell-price>${this.priceHtml(variant)}</div>
            <div class="mm-upsell__controls">
              ${showSelect ? `<select class="mm-upsell__select" data-mm-upsell-variant aria-label="Choose option">
                ${variants.map((v) => `<option value="${v.id}">${escapeHtml(v.title)}</option>`).join('')}
              </select>` : `<span style="flex:1"></span>`}
              <button type="button" class="mm-upsell__add" data-mm-upsell-add>Add</button>
            </div>
          </div>
        </div>
        ${multiple ? `<button type="button" class="mm-upsell__arrow mm-upsell__arrow--next" data-mm-upsell-nav="1" aria-label="Next product">${ICON_NEXT}</button>` : ''}
      </div>`;
  }

  onChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-mm-upsell-variant]')) return;
    const variant = this.selectedVariant();
    const price = this.querySelector('[data-mm-upsell-price]');
    if (price) price.innerHTML = this.priceHtml(variant);
    const img = this.querySelector('.mm-upsell__img img');
    const src = this.imageFor(this.product, variant);
    if (img instanceof HTMLImageElement && src) img.src = src;
  };

  onClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Re-rendering the slide detaches the clicked button; stop the click from reaching the
    // drawer's "clicked outside" check, which would otherwise close the drawer.
    if (target.closest('[data-mm-upsell-nav], [data-mm-upsell-add]')) event.stopPropagation();

    const nav = target.closest('[data-mm-upsell-nav]');
    if (nav instanceof HTMLElement) {
      const step = Number(nav.dataset.mmUpsellNav);
      this.index = (this.index + step + this.products.length) % this.products.length;
      this.render();
      return;
    }

    const add = target.closest('[data-mm-upsell-add]');
    if (add instanceof HTMLButtonElement) {
      const variant = this.selectedVariant();
      if (!variant) return;
      add.disabled = true;
      add.setAttribute('aria-busy', 'true');
      add.setAttribute('aria-label', 'Adding to cart');
      add.innerHTML = '<span class="mm-upsell__spinner" aria-hidden="true"></span>';
      const result = await mutateCart(add, {
        url: routes().cart_add_url || '/cart/add.js',
        body: { items: [{ id: variant.id, quantity: 1 }] },
        action: 'add',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
      });
      if (add.isConnected) {
        add.disabled = false;
        add.removeAttribute('aria-busy');
        add.removeAttribute('aria-label');
        add.textContent = 'Add';
      }
    }
  };
}

if (!customElements.get('mm-cart-upsell')) customElements.define('mm-cart-upsell', MMCartUpsell);
